import { AUTHENTICATION_METHOD, USERNAME_FORMAT_CODE, USER_ACTIVATION_METHOD } from '../../domain/domainCodes.js'
import { ApiError, api } from '../../lib/api'
import { isEnglish } from '../../lib/i18n'

/**
 * System settings — `Features/Admin/SettingsManagement/Settings/Endpoint.cs`.
 *
 *   getSettings -> GET /api/settings            -> SettingsPageViewModel (74 fields)
 *   saveSection -> POST /Settings/Save<Section> -> { Result, Message }
 *
 * TWENTY-TWO SAVE ACTIONS, ONE PER CARD. The screen was built that way already — every
 * card has its own save button — so `saveSection` keeps its signature and this module
 * does the routing. `SECTIONS` below is the whole map: a card's id, the action it posts
 * to, and the command shape that action binds.
 *
 * SHAPE. The server answers a FLAT view model (`ArabicApplicationTitle`,
 * `WebsiteFooterText`, …) while the screen reads a settings DICTIONARY keyed by the raw
 * setting names (`Default_ArabicApplicationTitle`, …). `toSettings` below is that
 * translation, and it is the only place the two vocabularies meet — the screen did not
 * change (R2).
 *
 * The `getAdvanced` and `listSettingsKeys` calls still read mock data; they belong to the
 * `AdvancedSettings` and `SettingsKeys` slices, which are integrated separately.
 */

/**
 * View model -> the dictionary `sectionDefaults` reads.
 *
 * The keys are the screen's, not the database's: this dictionary is a client-side shape,
 * and matching it exactly is what let the page stay untouched. Booleans become the
 * strings `"true"`/`"false"` because the selects are `<option value="true">`.
 */
function toSettings(dto) {
  const bool = (value) => (value ? 'true' : 'false')

  return {
    Default_ArabicApplicationTitle: dto.ArabicApplicationTitle ?? '',
    Default_EnglishApplicationTitle: dto.EnglishApplicationTitle ?? '',
    WebsiteFooterText: dto.WebsiteFooterText ?? '',
    WebsiteFooterTextEn: dto.WebsiteFooterTextEn ?? '',
    DomainURL: dto.DomainUrl ?? '',
    UseHsts: (dto.UseHsts ?? 'false').toLowerCase(),
    AdminPanelUserManualUrl: dto.AdminPanelUserManualUrl ?? '',
    ServicesUrl: dto.ServicesUrl ?? '',

    LoginPageTheme: dto.LoginPageTheme ?? 'white',
    LogoOnLoginPage: dto.LogoOnLoginPage ?? 'false',
    LogoBackgroundEnabled: dto.LogoBackground ?? 'true',

    ShowSupportContactInfoPage: bool(dto.ShowSupportContactInfoPage),
    ContactPageTitle: dto.ContactPageTitle ?? '',
    ContactPageTitleEn: dto.ContactPageTitleEn ?? '',
    ContactPageHeader: dto.ContactPageHeader ?? '',
    /* The screen reads a lower-case `h` here. Kept exactly as it asks rather than
       "corrected", because the two have to agree and the screen is the consumer. */
    ContactPageheaderEn: dto.ContactPageHeaderEn ?? '',
    ContactEmail: dto.ContactEmail ?? '',
    ContactEmailShowSendForm: bool(dto.ShowSupportContactSendEmailForm),
    ContactEmailShowEmailInfo: bool(dto.ShowSupportContactEmailInfo),
    ContactEmailShowPhoneInfo: bool(dto.ShowSupportContactPhoneInfo),
    ContactEmailLabelText: dto.ContactEmailLabelText ?? '',
    ContactEmailEnLabelText: dto.ContactEmailEnLabelText ?? '',
    ContactPhone: dto.ContactPhone ?? '',
    ContactPhoneLabelText: dto.ContactPhoneLabelText ?? '',
    ContactPhoneEnLabelText: dto.ContactPhoneEnLabelText ?? '',

    Default_EnablePlayerProgressbar: bool(dto.DefaultEnablePlayerProgressbar),
    Default_EnableForceSubtitle: bool(dto.DefaultEnableForceSubtitle),
    Default_EnablePlayerBlur: bool(dto.DefaultEnablePlayerPauseOnBlur),
    Default_EnablePlayerPlaybackRates: bool(dto.DefaultEnablePlayerPlaybackRates),
    EnableUserSubtitleControl: bool(dto.EnableUserSubtitleControl),

    Default_EnableUserManualPopup: bool(dto.DefaultEnableUserManualPopup),
    EnableIntroVideo: bool(dto.EnableIntroVideo),
    UserGuideLinkOnTheLoginPage: bool(dto.UserGuideLinkOnTheLoginPage),
    ShowUserGuideOnSeparatePage: bool(dto.ShowUserGuideOnSeparatePage),

    Email_Host: dto.EmailHost ?? '',
    Email_Port: dto.EmailPort ?? '',
    Email_From: dto.EmailFrom ?? '',
    Email_SenderName: dto.EmailSenderName ?? '',
    Email_Username: dto.EmailUsername ?? '',
    Email_Password: dto.EmailPassword ?? '',
    Email_SslOptions: dto.EmailSslOptions ?? '',
    Email_ServiceState: dto.EmailServiceState ?? '',
    DisableMailServerCertificateValidation: (dto.DisableMailServerCertificateValidation ?? 'false').toLowerCase(),

    /* Stored as JSON blobs, one per channel. Parsed at the edge so the cards read plain
       fields; `messagingBody` puts them back. */
    WhatsAppSettings: dto.WhatsAppSettings ?? '{}',
    SlackSettings: dto.SlackSettings ?? '{}',
    SmsSettings: dto.SmsSettings ?? '{}',

    User_Statistics_IsEnabled: bool(dto.UserStatisticsIsEnabled),
    Reports_Policies_Profile_Columns: dto.PoliciesReportProfileColumns ?? '',
    Reports_Exams_Profile_Columns: dto.ExamsReportProfileColumns ?? '',
  }
}

export async function getSettings(signal) {
  const dto = (await api.get('/api/settings', { signal })) ?? {}

  return {
    settings: toSettings(dto),
    brand: {
      main: dto.BrandPrimaryColor ?? '',
      active: dto.BrandActiveColor ?? '',
    },
    /* Only the culture-appropriate NAME comes back — the view model carries no Arabic
       name, English name or email, which is what the organization card needs to show
       what it is about to overwrite. See API_FINDINGS.md §16; `saveSection` refuses to
       post blanks because of it. */
    company: { name: dto.CompanyName ?? '', id: dto.CompanyId ?? 0 },
    /* The server already drops the three event types that carry no editable template
       (phishing, users, groups), so the client-side filter the mock needed is gone. */
    notificationTemplates: (dto.NotificationTemplates ?? []).map((template) => ({
      id: template.Id,
      // The table shows one name; which one depends on the panel's language.
      name: (isEnglish ? template.NameEnglish : template.NameArabic) || template.NameArabic || '',
      content: template.Content ?? '',
      type: template.Type,
      lastUpdated: template.LastUpdatedTxt ?? '',
    })),
  }
}

/**
 * Every card, the action it posts to, and how its form becomes that action's command.
 *
 * Adding a card means adding a row here and nothing else.
 */
const SECTIONS = {
  brandTheme: {
    path: '/Settings/SaveBrandTheme',
    body: (f) => ({ PrimaryColor: f.main, ActiveColor: f.active }),
  },
  appTitle: {
    path: '/Settings/SaveApplicationTitle',
    body: (f) => ({ ArabicTitle: f.arabicAppTitle, EnglishTitle: f.englishAppTitle }),
  },
  footer: {
    path: '/Settings/SaveFooterText',
    body: (f) => ({ ArabicFooter: f.footer, EnglishFooter: f.footerEn }),
  },
  domain: {
    path: '/Settings/SaveDomainUrl',
    body: (f) => ({ Url: f.domainUrl, UseHsts: String(f.useHsts) }),
  },
  loginTheme: {
    path: '/Settings/SaveLoginTheme',
    body: (f) => ({ Theme: f.theme, LogoOnLogin: String(f.logoOnLogin) }),
  },
  organization: {
    path: '/Settings/SaveOrganization',
    body: (f) => ({
      ArabicName: f.arabicOrgName,
      EnglishName: f.englishOrgName,
      Email: f.orgEmail,
    }),
    /* The page cannot show the current values (§16), so an untouched form is empty —
       and posting it would erase the organisation's name. Refuse instead. */
    guard: (f) => Boolean(f.arabicOrgName?.trim() && f.englishOrgName?.trim()),
  },
  contact: {
    path: '/Settings/SaveContactSettings',
    body: (f) => ({
      ShowContactPage: Boolean(f.enableContactPage),
      ShowSendEmailForm: Boolean(f.showSendForm),
      ShowEmailInfo: Boolean(f.showEmailInfo),
      ShowPhoneInfo: Boolean(f.showPhone),
      ContactPhone: f.contactPhone ?? '',
      ContactEmail: f.contactEmail ?? '',
      ContactPageTitle: f.contactPageTitle ?? '',
      ContactPageTitleEn: f.contactPageTitleEn ?? '',
      ContactPageHeader: f.contactPageHeader ?? '',
      ContactPageHeaderEn: f.contactPageHeaderEn ?? '',
      ContactEmailLabelText: f.contactEmailLabel ?? '',
      ContactEmailEnLabelText: f.contactEmailEnLabel ?? '',
      ContactPhoneLabelText: f.contactPhoneLabel ?? '',
      ContactPhoneEnLabelText: f.contactPhoneEnLabel ?? '',
    }),
  },
  video: {
    path: '/Settings/SaveVideoSettings',
    body: (f) => ({
      EnableProgressBar: Boolean(f.progressBar),
      EnableForceSubtitle: Boolean(f.forceSubtitle),
      EnablePauseOnBlur: Boolean(f.pauseOnBlur),
      EnablePlaybackRates: Boolean(f.playbackRates),
      EnableUserSubtitleControl: Boolean(f.userSubtitleControl),
    }),
  },
  userManual: {
    path: '/Settings/SaveUserManualSettings',
    body: (f) => ({
      EnableUserManualPopup: Boolean(f.manualPopup),
      EnableIntroVideo: Boolean(f.introVideo),
      UserGuideLinkOnTheLoginPage: Boolean(f.guideLinkOnLogin),
      ShowUserGuideOnSeparatePage: Boolean(f.guideSeparatePage),
    }),
  },
  adminGuide: {
    path: '/Settings/SaveAdminUserGuide',
    body: (f) => ({ AdminPanelUserManualUrl: f.adminManualUrl ?? '' }),
  },
  reports: {
    path: '/Settings/SaveReportSettings',
    body: (f) => ({
      // The server stores these as one comma-joined string per report.
      ReportPolicies: (f.policiesColumns ?? []).join(','),
      ReportExams: (f.examsColumns ?? []).join(','),
      UserStatisticsPage: String(Boolean(f.userStatistics)),
    }),
  },

  servicesUrl: {
    path: '/Settings/SaveServicesUrl',
    body: (f) => ({ ServicesUrl: f.servicesUrl ?? '' }),
  },
  email: {
    path: '/Settings/SaveEmailSettings',
    body: (f) => emailBody(f),
  },
  whatsapp: {
    path: '/Settings/SaveWhatsappSettings',
    body: (f) => ({
      Enabled: Boolean(f.whatsappEnabled),
      ApiUrl: f.whatsappApiUrl ?? '',
      ApiKey: f.whatsappApiKey ?? '',
      SenderNumber: f.whatsappSender ?? '',
    }),
  },
  slack: {
    path: '/Settings/SaveSlackSettings',
    body: (f) => ({
      Enabled: Boolean(f.slackEnabled),
      WebhookUrl: f.slackWebhook ?? '',
      BotToken: f.slackBotToken ?? '',
      Channel: f.slackChannel ?? '',
    }),
  },
  sms: {
    path: '/Settings/SaveSmsSettings',
    body: (f) => ({
      Enabled: Boolean(f.smsEnabled),
      ApiUrl: f.smsApiUrl ?? '',
      ApiKey: f.smsApiKey ?? '',
      SenderId: f.smsSenderId ?? '',
    }),
  },

  /* ---- إعدادات متقدمة ----------------------------------------------------
     Everything here reads AND writes: the advanced view model returns each of these,
     unlike the organisation and registration cards on the system-settings screen. */
  general: {
    path: '/AdvancedSettings/SaveGeneralSettings',
    body: (f) => ({
      CompanyId: Number(f.companyId ?? 1),
      AuthenticationId: Number(f.authenticationId ?? AUTHENTICATION_METHOD.LOCAL),
      AuthenticationMode: f.authenticationMode ?? 'Forms',
      UsernameFormatOptions: String(f.usernameFormat ?? USERNAME_FORMAT_CODE.UNCHANGED),
    }),
  },
  otp: {
    path: '/AdvancedSettings/SaveOtpSettings',
    body: (f) => ({
      Enabled: Boolean(f.otpEnabled),
      Target: f.otpTarget ?? 'Admins',
      Length: String(f.otpLength ?? '4'),
      ValidPeriod: String(f.otpValidPeriod ?? '120'),
    }),
  },
  lockout: {
    path: '/AdvancedSettings/SaveAccountLockoutSettings',
    body: (f) => ({
      EnableAccountLockout: Boolean(f.lockoutEnabled),
      AttemptsBeforeLockout: Number(f.attempts ?? 5),
      AccountLockoutWindow: Number(f.lockoutWindow ?? 5),
      SessionTimeoutInMinutes: Number(f.sessionTimeout ?? 600),
    }),
  },
  terms: {
    path: '/AdvancedSettings/UpdateTerms',
    body: (f) => ({
      Arabic: f.termsAr ?? '',
      English: f.termsEn ?? '',
      IsEnabled: Boolean(f.termsEnabled),
      /* Marks the document as CHANGED, which makes everyone who already accepted it
         accept again — see the terms gate. Never inferred; the admin ticks it. */
      IsUpdated: Boolean(f.termsUpdated),
    }),
    /* Never post an empty document. This is the text every user in the installation has
       to accept, and the update replaces it outright — an empty save would leave the
       terms gate showing a blank page nobody can meaningfully agree to. */
    guard: (f) => Boolean(f.termsAr?.trim() || f.termsEn?.trim()),
  },
  /* THE SECOND CARD THAT HAD NO ROW HERE. `SaveRegistrationSettings` has existed in the
     Settings slice all along and binds exactly the seven fields this card collects; without
     the row, saving it reported "this setting is not available yet" — the same symptom the
     side-image card had, from the same cause.

     `activationBy` is the select's own `'1' | '2'`, seeded from `company.activateByAdmin`
     (AdvancedSettingsPage:1045). The command splits that one choice back into the two
     booleans the `Companies` row stores. */
  registration: {
    path: '/Settings/SaveRegistrationSettings',
    body: (f) => ({
      ActivateInvitations: Boolean(f.activateInvitations),
      ActivateResetPasswordPage: Boolean(f.activateResetPassword),
      ShowRegistrationLinkOnLoginPage: Boolean(f.showRegistrationLink),
      ActivateUserByAdmin: String(f.activationBy) === String(USER_ACTIVATION_METHOD.ADMIN),
      ActivateUserByEmail: String(f.activationBy) !== String(USER_ACTIVATION_METHOD.ADMIN),
      UserEmailOnDomain: Boolean(f.emailOnDomain),
      CompanyDomains: f.companyDomains ?? '',
    }),
  },
  otherProperties: {
    path: '/AdvancedSettings/UpdateProfileOtherProperties',
    body: (f) => ({
      ShowRegistrationLinkOnLoginPage: Boolean(f.showRegistrationLink),
      ActivateResetPasswordPage: Boolean(f.activateResetPassword),
      ActivateInvitations: Boolean(f.activateInvitations),
    }),
  },
  integration: {
    path: '/AdvancedSettings/SaveADIntegrationSettings',
    body: (f) => ({
      DepsDataSource: f.deptsSource ?? 'None',
      DepsIntegrationType: f.deptsType ?? 'Import',
      UsersDataSource: f.usersSource ?? 'None',
      UsersIntegrationType: f.usersType ?? 'Sync',
      UsersFetchDataSource: f.userFetchSource ?? 'None',
      UsersFetchIntegrationType: f.userFetchType ?? 'Sync',
    }),
  },
  autoIntegration: {
    path: '/AdvancedSettings/SaveAutoIntegrationSettings',
    body: (f) => ({
      AutoDeptsEnabled: String(f.deptsAutoFetch) === 'true',
      AutoDeptsSchedule: String(f.deptsAutoFetchInterval ?? ''),
      AutoUsersEnabled: String(f.usersAutoFetch) === 'true',
      AutoUsersSchedule: String(f.usersAutoFetchInterval ?? ''),
    }),
  },
  services: {
    path: '/AdvancedSettings/SetServicesSettings',
    body: (f) => ({
      ServiceEnabledNotifications: String(f.ServiceEnabledNotifications) === 'true',
      MaxTaskExecutionMinutes: String(f.MaxTaskExecutionMinutes ?? '10'),
      MaxRetries: String(f.MaxRetries ?? '3'),
    }),
  },

  /**
   * The profile fields, as a whole list.
   *
   * The card edits two sets of names — visible and editable — but the action replaces
   * every field's three flags at once, so the full list has to be rebuilt from what was
   * loaded. That is why this one takes the page's data as context: a body built from the
   * form alone would drop `IsRequired` and every field the card does not show.
   */
  profileFields: {
    path: '/AdvancedSettings/Update',
    body: (f, context) => ({
      Fields: (context?.profileFields ?? []).map((field) => ({
        ID: field.id,
        FieldName: field.name,
        IsVisible: (f.visibleFields ?? []).includes(field.name),
        IsEditable: (f.editableFields ?? []).includes(field.name),
        // Not on this card; carried through so saving does not clear it.
        IsRequired: Boolean(field.isRequired),
      })),
    }),
    guard: (f, context) => (context?.profileFields ?? []).length > 0,
  },

  /* Exactly five, ids 1-5 — the handler rejects any other count and switches on the id
     to pick the settings key pair. */
  customAttributes: {
    path: '/AdvancedSettings/SetCustomAttributes',
    body: (f) => ({
      Attributes: [1, 2, 3, 4, 5].map((index) => ({
        ID: index,
        AR_Name: f[`attribute${index}Ar`] ?? '',
        EN_Name: f[`attribute${index}En`] ?? '',
      })),
    }),
  },

  /* ---- the image cards ---------------------------------------------------
     `SaveLogo` takes the file inline — base64 content plus its name, size and
     type — rather than a multipart upload, so these post JSON like everything
     else and never touch `upload.js`. */
  logo: {
    path: '/Settings/SaveLogo',
    file: 'logo',
    body: async (f) => ({
      ...(await fileFields(f.logo)),
      LogoBackground: String(f.logoBackground ?? 'true'),
      IsReporting: false,
    }),
  },
  reportLogo: {
    path: '/Settings/SaveLogo',
    file: 'reportLogo',
    body: async (f) => ({
      ...(await fileFields(f.reportLogo)),
      LogoBackground: String(f.logoBackground ?? 'true'),
      IsReporting: true,
    }),
  },
  favicon: {
    path: '/Settings/SaveFavIcon',
    file: 'favicon',
    body: async (f) => ({ FavIcon: await asBase64(f.favicon) }),
  },
  backgroundLogin: {
    path: '/Settings/SaveLoginBackground',
    body: async (f) => ({
      BackgroundAr: await asBase64(f.backgroundAr),
      BackgroundEn: await asBase64(f.backgroundEn),
    }),
    guard: (f) => Boolean(f.backgroundAr || f.backgroundEn),
  },
  /* The side images beside the sign-in form. The port carried `SaveLogo`, `SaveFavIcon` and
     `SaveLoginBackground` over from the legacy settings controller and left `UpdateSideLogin`
     behind, so this card had no row here and reported itself unavailable. The endpoint exists
     now — `SaveLoginSideImage`, the same two fixed filenames the legacy writes. */
  sideLogin: {
    path: '/Settings/SaveLoginSideImage',
    body: async (f) => ({
      Arabic: await asBase64(f.sideLoginAr),
      English: await asBase64(f.sideLoginEn),
    }),
    guard: (f) => Boolean(f.sideLoginAr || f.sideLoginEn),
  },
  userGuide: {
    path: '/Settings/SaveUserGuide',
    body: async (f) => ({
      Arabic: await asBase64(f.guideAr),
      English: await asBase64(f.guideEn),
    }),
    guard: (f) => Boolean(f.guideAr || f.guideEn),
  },
}

/**
 * @returns {Promise<{ ok: boolean, message: string }>} `ok:false` with the server's own
 *          words when it refused, so the card can say why rather than just stopping.
 */
export async function saveSection(section, values, context) {
  const spec = SECTIONS[section]

  // A card with no endpoint behind it — see COSMETIC_UI.md.
  if (!spec) return { ok: false, message: '', unsupported: true }

  if (spec.file && !values[spec.file]) return { ok: false, message: '', nothingToSave: true }
  if (spec.guard && !spec.guard(values, context)) return { ok: false, message: '', nothingToSave: true }

  try {
    const data = await api.post(spec.path, await spec.body(values, context))
    return { ok: true, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** The eight fields `SaveEmailSettings` binds. `TestEmailSettings` takes the same, plus one. */
function emailBody(f) {
  return {
    Host: f.emailHost ?? '',
    Port: String(f.emailPort ?? ''),
    From: f.emailFrom ?? '',
    SenderName: f.emailSenderName ?? '',
    Username: f.emailUsername ?? '',
    Password: f.emailPassword ?? '',
    SslOptions: f.emailSslOptions ?? '',
    ServiceState: String(f.emailServiceState ?? ''),
  }
}

/**
 * Sends a probe message with the settings CURRENTLY IN THE FORM, not the stored ones —
 * which is the point: it answers "will this work" before anyone commits it.
 *
 * `address` is where the probe lands. Left empty the server sends to the sender address,
 * which is what this action did before it took one.
 */
export async function testEmailSettings(values, address = '') {
  try {
    const data = await api.post('/Settings/TestEmailSettings', {
      ...emailBody(values),
      DisableServerCertificateValidation: String(values.disableCertValidation) === 'true',
      Address: address ?? '',
    })
    return { ok: true, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** One channel's stored JSON blob, as plain fields. */
export function parseChannel(raw) {
  try {
    const parsed = JSON.parse(raw || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    // An administrator can put anything in a settings row; a broken blob is an empty card.
    return {}
  }
}

/** `SaveLogoCommand` wants the content, the name, the size and the MIME type. */
async function fileFields(file) {
  return {
    File: await asBase64(file),
    Name: file?.name ?? '',
    Size: file?.size ?? 0,
    Type: file?.type ?? '',
  }
}

/** Base64 WITHOUT the `data:` prefix, which is what the handlers decode. */
function asBase64(file) {
  if (!file) return Promise.resolve('')

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read the selected file'))
    reader.onload = () => resolve(String(reader.result).split(',').pop() ?? '')
    reader.readAsDataURL(file)
  })
}

/* ---- still mocked: their own slices ------------------------------------- */

/**
 * `إعدادات متقدمة` — `Features/Admin/SettingsManagement/AdvancedSettings/Endpoint.cs`.
 *
 *   getAdvanced -> GET /api/advanced-settings
 *
 * The data sources and file-storage servers still come from the mock: they have their own
 * GET actions (`/AdvancedSettings/GetDataSources`, `/GetFileStorageServers`) and their own
 * create / delete / test flows, which are wired with the collection cards in part 2.
 */
export async function getAdvanced(signal) {
  const dto = (await api.get('/api/advanced-settings', { signal })) ?? {}
  const bool = (value) => (value ? 'true' : 'false')

  /* Two collections have their own GET actions rather than riding on the page payload,
     so they are fetched alongside it. Both are small and neither blocks the other. */
  const [dataSources, servers] = await Promise.all([
    api.get('/AdvancedSettings/GetDataSources', { signal }).catch(() => []),
    api.get('/AdvancedSettings/GetFileStorageServers', { signal }).catch(() => []),
  ])

  return {
    settings: {
      LicenseType: dto.LicenseType ?? '',
      AuthenticationMode: dto.AuthenticationMode ?? 'Forms',
      UsernameFormatOptions: dto.UsernameFormatOptions ?? USERNAME_FORMAT_CODE.UNCHANGED,

      OTPEnabled: bool(dto.OtpEnabled),
      OTPTarget: dto.OtpTarget ?? 'Admins',
      OTPLength: dto.OtpLength ?? '4',
      OTPValidPeriod: dto.OtpValidPeriod ?? '120',

      EnableAccountLockout: bool(dto.EnableAccountLockout),
      AttemptsBeforeLockout: String(dto.AttemptsBeforeLockout ?? 5),
      AccountLockoutWindow: String(dto.AccountLockoutWindow ?? 5),
      SessionTimeoutInMinutes: String(dto.SessionTimeoutInMinutes ?? 600),

      DepartmentsIntegrationDataSource: dto.DepartmentsIntegrationDataSource ?? 'None',
      DepartmentsIntegrationType: dto.DepartmentsIntegrationType ?? 'Import',
      UsersIntegrationDataSource: dto.UsersIntegrationDataSource ?? 'None',
      UsersIntegrationType: dto.UsersIntegrationType ?? 'Sync',
      UserFetchInfoDataSource: dto.UserFetchInfoDataSource ?? 'None',
      UserFetchInfoType: dto.UserFetchInfoType ?? 'Sync',

      DeptsAutoFetch: dto.DeptsAutoFetch ?? 'false',
      DeptsAutoFetchInterval: dto.DeptsAutoFetchInterval ?? '180',
      UsersAutoFetch: dto.UsersAutoFetch ?? 'false',
      UsersAutoFetchInterval: dto.UsersAutoFetchInterval ?? '1200',
      WhenFetchedUsersCountGreaterThanLicense: dto.WhenFetchedUsersCountGreaterThanLicense ?? 'Reject',
      DeleteADGroupsWithNoUsers: bool(dto.DeleteAdGroupsWithNoUsers),
      SyncProfilesCommandMode: dto.SyncProfilesCommandMode ?? 'bulkSql',

      /* The card reads these five pairs by settings-key name. The server returns them
         as a list, so they are spread back out here. */
      ...Object.fromEntries(
        (dto.CustomAttributes ?? []).flatMap((attribute) => [
          [`Attribute${attribute.ID ?? attribute.Id}_AR_Name`, attribute.AR_Name ?? ''],
          [`Attribute${attribute.ID ?? attribute.Id}_EN_Name`, attribute.EN_Name ?? ''],
        ]),
      ),

      ActivateInvitations: bool(dto.ActivateInvitations),
      ShowRegistrationLinkOnLoginPage: bool(dto.ShowRegistrationLinkOnLoginPage),
      ActivateResetPasswordPage: bool(dto.ActivateResetPasswordPage),
    },
    company: {
      id: dto.CompanyId ?? 1,
      authenticationId: dto.AuthenticationId ?? AUTHENTICATION_METHOD.LOCAL,
    },
    /* The three flags on the profile card ARE readable here — unlike the same settings on
       the system-settings screen, which is why the registration card lives on this page. */
    profileOther: {
      allowProfileEditPage: Boolean(dto.AllowProfileEditPage),
      enableForceGroupChange: Boolean(dto.EnableForceGroupChange),
      forceChangeGroupId: dto.ForceChangeGroupId ?? '',
    },
    /* `TermsOfUse`, not `Terms` — reading the wrong property here left the card empty,
       and its save would then have blanked the document every user has to accept. */
    terms: {
      arabic: dto.TermsOfUse?.Arabic ?? '',
      english: dto.TermsOfUse?.English ?? '',
      isEnabled: Boolean(dto.TermsOfUse?.IsEnabled),
      isUpdated: Boolean(dto.TermsOfUse?.IsUpdated),
    },
    profileFields: (dto.UserProfileFields ?? []).map((field) => ({
      id: field.ID ?? field.Id,
      name: field.BuiltInAttributeName ?? '',
      isVisible: Boolean(field.IsVisible),
      isRequired: Boolean(field.IsRequired),
      isEditable: Boolean(field.IsEditable),
    })),
    customAttributes: (dto.CustomAttributes ?? []).map((attribute) => ({
      id: attribute.ID ?? attribute.Id,
      arabicName: attribute.AR_Name ?? '',
      englishName: attribute.EN_Name ?? '',
    })),
    authMethods: (dto.AuthenticationMethods ?? []).map((method) => ({
      id: method.AuthenticationId,
      name: method.AuthenticationId === AUTHENTICATION_METHOD.ENTRA_ID
        ? 'Microsoft Entra ID'
        : method.AuthenticationId === AUTHENTICATION_METHOD.ENTRA_ID_AND_LOCAL
          ? 'Microsoft Entra ID + Local'
          : method.AuthenticationName ?? '',
    })),
    groups: (dto.Departments ?? []).map((group) => ({
      id: group.GroupId,
      name: (isEnglish ? group.EnglishName : group.ArabicName) || group.ArabicName || '',
    })),
    dataSources: (dataSources ?? []).map((source) => ({
      id: source.Id,
      type: source.Type ?? '',
      arabicName: source.ArabicName ?? '',
      englishName: source.EnglishName ?? '',
      username: source.Username ?? '',
      connectionString: source.ConnectionString ?? '',
      domain: source.Domain ?? '',
      filters: source.Filters ?? '',
      ssl: Boolean(source.SSL),
      isActive: Boolean(source.IsActive),
      hasCredential: Boolean(source.HasCredential),
      limitAccessToGroup: source.LimitAccessToGroup ?? '',
      adminGroups: source.AdminGroups ?? '',
    })),
    servers: (servers ?? []).map((server) => ({
      id: server.Id,
      serverId: server.ServerId,
      serverAddress: server.ServerAddress ?? '',
    })),
  }
}

export async function saveDataSource(values) {
  try {
    const data = await api.post('/AdvancedSettings/SaveDataSource', {
      Id: Number(values.id || 0),
      Name: values.name?.trim() ?? '',
      Type: values.type ?? 'LDAP',
      ConnectionString: values.connectionString?.trim() ?? '',
      Username: values.username?.trim() ?? '',
      Password: values.password ?? '',
      Domain: values.domain?.trim() ?? '',
      Filters: values.filters?.trim() ?? '',
      SSL: Boolean(values.ssl),
      IsActive: Boolean(values.isActive),
      LimitAccessToGroup: values.limitAccessToGroup?.trim() ?? '',
      AdminGroups: values.adminGroups?.trim() ?? '',
    })
    return { ok: true, message: data?.Message ?? '' }
  } catch (error) {
    return { ok: false, message: error instanceof ApiError ? error.message : '' }
  }
}

export async function testDirectorySource(values) {
  try {
    const data = await api.post('/AdvancedSettings/TestActiveDirectory', {
      Id: Number(values.id || 0),
      Type: values.type ?? 'LDAP',
      ConnectionString: values.connectionString?.trim() ?? '',
      Username: values.username?.trim() ?? '',
      Password: values.password ?? '',
      Domain: values.domain?.trim() ?? '',
      Filters: values.filters?.trim() ?? '',
      SSL: Boolean(values.ssl),
    })
    return { ok: true, message: data?.Message ?? '', properties: data?.Properties ?? [] }
  } catch (error) {
    return { ok: false, message: error instanceof ApiError ? error.message : '' }
  }
}

/**
 * `بيانات الإعدادات` — every settings key with its stored and default value, plus the
 * reference tables the platform's enums are supposed to mirror.
 *
 * The second part is a CONSISTENCY CHECK, not settings: each enum the code compiles
 * against is listed against the database rows that should back it, so a missing activity
 * type or authentication method is visible here rather than as a blank label somewhere
 * else. The Razor page showed it; the React screen did not, and now does.
 */
export async function listSettingsKeys(signal) {
  const dto = (await api.get('/api/settings-keys', { signal })) ?? {}

  const reference = (rows, group, name = (r) => (isEnglish ? r.DbEnglishName : r.DbArabicName)) =>
    (rows ?? []).map((row) => ({
      group,
      id: row.Id,
      enumName: row.EnumName ?? '',
      dbName: name(row) ?? '',
      existsInDb: Boolean(row.ExistsInDb),
    }))

  return {
    rows: (dto.Settings ?? []).map((row) => ({
      key: row.Key,
      // `null` and `""` mean different things here: never written vs written empty.
      dbValue: row.DbValue ?? null,
      defaultValue: row.DefaultValue ?? null,
    })),
    reference: [
      ...reference(dto.ActivityEntries, 'activities'),
      ...reference(dto.GroupTypes, 'groupTypes'),
      ...reference(dto.DifficultyLevels, 'difficultyLevels'),
      ...reference(dto.CourseLevels, 'courseLevels'),
      ...reference(dto.AuthenticationMethods, 'authMethods', (r) => r.DbName),
      ...reference(dto.NotificationTemplates, 'templates'),
    ],
    /* Whether the video toolchain is present. A false here explains a whole class of
       "the video did not process" reports, so it is worth a line on this page. */
    ffmpegExists: Boolean(dto.FfmpegExists),
  }
}
